import { Injectable } from '@angular/core';

import { Job, ModelConverterOptions, PcvizUploadConfig } from '@mirage-projects/cw-models';
import { DataAccessPcvizService, JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { LogService } from '../log';

export enum SupportedModelConverters {
    OBJ_UPLOADER = 'OBJU',
}

/**
 * Service to launch the model converter job from the endpoint
 */
@Injectable({ providedIn: 'root' })
export class ModelConverterService {
    constructor(
        private dataAccessPcvizService: DataAccessPcvizService,
        private jobPcvizService: JobPcvizService,
        private logService: LogService
    ) {}

    /**
     * Calls the model converter endpoint using only the upload configuration.
     * Extracts all necessary data from the config (files, layer ID, scale factor, etc.)
     * @param config The upload configuration containing all necessary data
     * @returns A promise that resolves to the job information.
     */
    async callModelConverterEndpoint(config: PcvizUploadConfig): Promise<Job> {
        const files = config.files || [];
        const layerId = config.object?.id;
        const scaleFactor = this.getScaleFactorFromConfig(config);

        if (!layerId) {
            this.logService.error('No layer ID found in config - cannot launch model converter job');
            throw new Error('Layer ID is required for model converter job');
        }

        const inputData = {
            files: files.map((file) => `process/${file.name}`),
            fileSizes: files.map((f) => f.size),
            ScaleFactor: scaleFactor,
        };

        const layerDriver = this.dataAccessPcvizService.getDriver('da.layer');
        const jobName = 'model-converter';

        try {
            const { job } = await layerDriver.launchWorkflowJob(layerId, inputData, jobName);
            return job;
        } catch (error) {
            this.logService.error('Error launching model converter job:', error);
            throw error;
        }
    }

    /**
     * Determines if the current job adapter is set for 3D model conversion.
     * @returns True if the job adapter is for 3D model conversion, false otherwise.
     */
    isModel3dConversion(): boolean {
        const jobAdapter = this.jobPcvizService.getJobAdapter();
        if (!jobAdapter) {
            return false;
        }
        const adapterId = jobAdapter.getId();
        return Object.values(SupportedModelConverters).includes(adapterId as SupportedModelConverters);
    }

    private getScaleFactorFromConfig(config: PcvizUploadConfig): number {
        if (config.jobAdapter) {
            const converterOptions: ModelConverterOptions = config.jobAdapter.getConverterOptions?.();
            if (converterOptions?.ScaleFactor) {
                return converterOptions.ScaleFactor;
            }
        }
        return 1.0;
    }
}
